import React from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { Benefit, CategoryCoverage } from '../../types/insurance-report-api.types';
import { constructCategoryCoverage, DEFAULT_ACCORDION_ID } from '../../utils/constants';
import { titleCase } from '../../utils/utils';
import { AccordionTable } from '../common-components/accordion-table';
import { EmptyStates } from '../common-components/empty-state';

type FrequencyAndLimitationsProps = {
  benefits: Record<string, Benefit[]>;
  //categoryCoverages object come from tuuthfairy with specific order we want to render our categories in the same order so  iterating through categoryCoverages
  categoryCoverages: CategoryCoverage[];
  hasFavoriteCDTCodes?: boolean;
};

export const FrequencyAndLimitations = React.memo(
  ({ benefits, categoryCoverages, hasFavoriteCDTCodes }: FrequencyAndLimitationsProps) => {
    const hasBenefits = Object.keys(benefits || {}).length;

    const { t } = useTranslation('insurance-eligibility-report');
    /* Below condition flow executes only when `categoryCoverages` is empty but the `benefits` object contains data.
        It indicates that the selected network has 0% coverage which is the reason that record is not present in `categoryCoverages`,
        yet `benefits` still has entries (all with 0% coverage).
        To ensure the data is displayed as-is, we render the benefits directly in this case.
    */
    const showBenefitsWithZeroCoverage = !categoryCoverages?.length && hasBenefits;
    return (
      <div css={containerStyles}>
        <Heading level={3} css={headerStyles}>
          {hasFavoriteCDTCodes ? t('All Procedures') : t('Frequency & Limitations')}
        </Heading>
        {!hasBenefits && <EmptyStates description={t('No data found')} />}
        {(categoryCoverages || []).map((coverage, index) => {
          if (!benefits[coverage.category] || benefits[coverage.category].length === 0) {
            return null; // Skip categories with no benefits
          }
          return (
            <AccordionTable
              key={coverage.category}
              accordionId={coverage.category || DEFAULT_ACCORDION_ID}
              accordionTitle={titleCase(coverage.category)}
              accordionCoveragePercentage={constructCategoryCoverage(coverage.minCoverage, coverage.maxCoverage)}
              data={benefits[coverage.category]}
              customTableStyles={index === categoryCoverages.length - 1 ? customTableStyles : undefined}
            />
          );
        })}
        {!!showBenefitsWithZeroCoverage &&
          Object.entries(benefits).map(([category, benefits]) => {
            if (benefits?.length === 0) return null;

            return (
              <AccordionTable
                key={category}
                accordionId={category || DEFAULT_ACCORDION_ID}
                accordionTitle={titleCase(category)}
                accordionCoveragePercentage={'0%'}
                data={benefits}
              />
            );
          })}
      </div>
    );
  }
);

FrequencyAndLimitations.displayName = 'FrequencyAndLimitations';

const headerStyles = css`
  display: flex;
  align-items: center;
  padding-left: ${theme.spacing(2)};
  height: 40px;
  background-color: ${theme.colors.primary5};
  border: 1px solid ${theme.colors.primary20};
  border-radius: ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0;
`;
//applying the bottom border radius to heading only when empty state image is rendered
const containerStyles = css`
  h3:has(~ div img) {
    border-bottom-left-radius: ${theme.borderRadius.medium};
    border-bottom-right-radius: ${theme.borderRadius.medium};
  }
  /*Below styling is for the last accordion button */
  > div:last-of-type,
  > div:last-of-type > div {
    border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
    button[aria-expanded='false'] {
      border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
    }
  }
`;
const customTableStyles = css`
  border-bottom-left-radius: ${theme.borderRadius.medium} !important;
  border-bottom-right-radius: ${theme.borderRadius.medium} !important;
`;
